import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { Pagination } from './components/Pagination';
import { SortableTableHeaders } from './components/SortableHeaders';
import { Order } from './components/sortingFunctions';
import Loader from '@act/react-components/workflows/loader';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { usePaginationAndSort } from './PaginationAndSortHook';
import { useMobile } from '@act/utilities/mobile-context';
import {
  PaginationOrSortParameters,
  MenuItemConfiguration,
  ColumnConfiguration,
} from './types';

export type { PaginationOrSortParameters, MenuItemConfiguration, ColumnConfiguration };

export interface MenuConfiguration<T> {
  anchorEl: HTMLElement;
  open: boolean;
  menuItemConfigurations: MenuItemConfiguration[];
  openAdditionalMenu: (event: React.MouseEvent<HTMLElement>, data: T) => void;
  onClose: () => void;
}

export type InMemoryPaginationProps = {
  inMemoryPagination: true;
};

export type NonMemoryPaginationProps = {
  inMemoryPagination: false;
  onPaginationOrSort: (parameters: PaginationOrSortParameters) => void;
  page: number;
  status: 'failed' | 'initial' | 'loading' | 'success';
  count: number;
  rowsPerPageOptions: number[];
  rowsPerPage: number;
};

export type TableProps<T> = {
  data: T[];
  inMemoryPagination: boolean;
  rowsPerPageOptions?: number[];
  menuConfiguration?: MenuConfiguration<T>;
  onRowClick?: (row: T) => void;
  columnConfig: ColumnConfiguration<T>[];
  initialSortBy?: keyof T;
  initialSortDirection?: Order;
};

export type PaginatedTableProps<T> =
  | (TableProps<T> & InMemoryPaginationProps)
  | (TableProps<T> & NonMemoryPaginationProps);

export const PaginatedTable = <T,>(props: PaginatedTableProps<T>) => {
  const { isMobile } = useMobile();
  const {
    data,
    inMemoryPagination,
    columnConfig,
    rowsPerPageOptions = [25, 50, 100],
    menuConfiguration,
    onRowClick,
    initialSortBy,
    initialSortDirection,
  } = props;

  const [orderDirection, setOrderDirection] = useState<Order>(
    initialSortDirection || 'asc'
  );
  const [orderBy, setOrderBy] = useState<keyof T>(initialSortBy || null);

  const {
    paginatedData,
    page: inMemoryPage,
    rowsPerPage: inMemoryRowsPerPage,
    onPaginationOrSort: handleInMemoryPaginationOrSort,
  } = usePaginationAndSort({
    initialData: data,
    initialRowsPerPage: rowsPerPageOptions[0],
  });

  // Set table props based on pagination type
  const nonMemoryPagination = inMemoryPagination === false;
  const currentData = nonMemoryPagination ? data : paginatedData;
  const page = nonMemoryPagination ? props.page : inMemoryPage;
  const count = nonMemoryPagination ? props.count : data.length;
  const status = nonMemoryPagination ? props.status : undefined;
  const rowsPerPage = nonMemoryPagination
    ? props.rowsPerPage
    : inMemoryRowsPerPage;
  const onPaginationOrSort = nonMemoryPagination
    ? props.onPaginationOrSort
    : handleInMemoryPaginationOrSort;

  function handleRequestSort(
    _event: React.MouseEvent<unknown>,
    property: string
  ) {
    const isAsc = orderBy === property && orderDirection === 'asc';
    const sortOrder = isAsc ? 'desc' : 'asc';
    setOrderBy(property as keyof T);
    setOrderDirection(sortOrder);
    onPaginationOrSort({ property, sortOrder });
  }

  return (
    <Box
      sx={{
        position: 'relative',
        overflowY: isMobile ? 'visible' : 'auto',
        width: 'auto',
        height: isMobile ? 'auto' : '100%',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: isMobile ? 'none' : '100%',
        maxWidth: 'auto',
      }}
    >
      <Box
        component={Paper}
        elevation={0}
        sx={{
          position: 'relative',
          overflowX: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: isMobile ? 'auto' : '100%',
          maxHeight: isMobile ? 'none' : '100%',
          width: 'auto',
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            paddingBottom: 0,
            marginBottom: 0,
            minWidth: isMobile ? '800px' : 'auto', // Ensure table has minimum width on mobile
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#a8a8a8',
            },
          }}
        >
          {status && ['loading', 'initial'].includes(status) && <Loader />}
          {((status && status === 'success') || status === undefined) && (
            <Table
              stickyHeader
              sx={{
                width: 'auto',
                tableLayout: 'fixed',
                marginBottom: 0,
                position: 'relative',
                minWidth: '100%',
                '& .MuiTableCell-root': {},
                '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root':
                  {
                    paddingBottom: '0px',
                  },
                '& .MuiTableHead-root': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  backgroundColor: 'white',
                },
              }}
            >
              <SortableTableHeaders
                columnConfig={columnConfig}
                order={orderDirection}
                orderBy={String(orderBy)}
                onRequestSort={handleRequestSort}
                hasActionCell={!!menuConfiguration}
              />
              <TableBody
                sx={{
                  '.MuiTableRow-root:last-child td, .MuiTableRow-root:last-child th':
                    { border: 0 },
                }}
              >
                {currentData.map((row, index) => {
                  return (
                    <TableRow
                      key={`data-${index}`}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(25, 28, 29, 0.08)',
                          cursor: 'pointer',
                        },
                      }}
                      onClick={onRowClick ? () => onRowClick(row) : null}
                    >
                      {columnConfig.map(column => {
                        const key = `${String(column.id)}-${index}`;
                        return (
                          <TableCell
                            key={key}
                            align={
                              column.align ||
                              (column.isNumeric ? 'right' : 'left')
                            }
                            sx={{
                              width: column.width || 'auto',
                              minWidth: column.width || 'fit-content',
                              whiteSpace: column.noWrap ? 'nowrap' : 'normal',
                            }}
                          >
                            {typeof column.value === 'function' &&
                              column.value(row)}
                            {typeof column.value !== 'function' && (
                              <Typography component="div">
                                {String(row[column.id])}
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                      {menuConfiguration && (
                        <TableCell align="right">
                          <IconButton
                            children={<MoreVertIcon />}
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              menuConfiguration.openAdditionalMenu(e, row);
                            }}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {menuConfiguration && (
                  <Menu
                    anchorEl={menuConfiguration?.anchorEl}
                    open={menuConfiguration?.open}
                    onClose={menuConfiguration?.onClose}
                  >
                    {menuConfiguration?.menuItemConfigurations?.map(
                      menuItem => {
                        return (
                          <MenuItem
                            key={menuItem.label}
                            onClick={menuItem.onClick}
                            disabled={menuItem.isDisabled}
                          >
                            {menuItem.label}
                          </MenuItem>
                        );
                      }
                    )}
                  </Menu>
                )}
              </TableBody>
            </Table>
          )}
        </Box>
        <Box sx={{ flexShrink: 0, marginTop: 0 }}>
          <Pagination
            count={count}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={rowsPerPageOptions}
            page={page}
            onPaginationOrSort={onPaginationOrSort}
          />
        </Box>
      </Box>
    </Box>
  );
};
